/**
 * The navigation model, as data.
 *
 * S-D3 approved a sidebar with two modules and eight-ish sections each; almost
 * none of those sections has a screen yet, and the sessions that build them are
 * named beside each entry below. Two ways to handle that were available and
 * only one is honest:
 *
 *  * **Hide what does not exist.** The sidebar then grows silently over a dozen
 *    sessions and is never laid out at its real size until the last one lands —
 *    and the approved information architecture is invisible to anyone reviewing
 *    the shell.
 *  * **Render it disabled.** The model is visible and testable at full size,
 *    and "not available yet" is a state, announced in text (`aria-disabled`
 *    plus a visible label) rather than by colour.
 *
 * The second. What makes it not a placeholder is that there is nothing left to
 * wire up here: the session that builds a module adds its route and flips
 * `to`, and this file is the only edit the shell needs.
 */

export type ModuleId = 'outreach' | 'seo'

export interface NavSection {
  label: string
  /** Absent while the module that owns this section has not shipped. */
  to?: string
  /** The session that brings it — for the reader of this file, not the user. */
  arrivesIn: string
}

export interface ModuleDefinition {
  id: ModuleId
  label: string
  /** Where the switcher lands when this module is chosen. */
  home: string
  sections: NavSection[]
}

export const MODULES: readonly ModuleDefinition[] = [
  {
    id: 'outreach',
    label: 'Outreach',
    home: '/outreach',
    sections: [
      { label: 'Overview', to: '/outreach', arrivesIn: 'S-B8' },
      // S-B20. The libraries behind the leads list have existed since S-B13 and
      // no screen read them; this is the session that gave them one, which is
      // the single edit this file's own docstring says a module's session makes.
      { label: 'Leads', to: '/outreach/leads', arrivesIn: 'S-B13' },
      { label: 'Inbox', to: '/outreach/inbox', arrivesIn: 'S-B19' },
      { label: 'Pipeline', to: '/outreach/pipeline', arrivesIn: 'S-B20' },
      // S-B21. `outreach#packs` — the last disabled entry in either module, and
      // this file's own docstring says flipping it is the single edit a module's
      // session makes here. **The label stays *Niche packs*** even though the
      // screen's h1 is *What we search for*: S-D3 approved seven Outreach
      // sections by name and renaming one is not this session's call.
      { label: 'Niche packs', to: '/outreach/packs', arrivesIn: 'S-B21' },
      { label: 'Suppression', to: '/outreach/suppression', arrivesIn: 'S-B12' },
      { label: 'Settings', to: '/settings/organisation', arrivesIn: 'S-B8' },
    ],
  },
  {
    id: 'seo',
    label: 'SEO',
    home: '/seo',
    sections: [
      { label: 'Overview', to: '/seo', arrivesIn: 'S-B8' },
      // S-B22a. The property picker and the figures it produces. Named
      // *Search performance* rather than *Sites* because that is what the
      // screen is about — S-B30's uptime pings and S-B23's audit are the rest
      // of what a *Sites* section would eventually hold.
      { label: 'Search performance', to: '/seo/search-performance', arrivesIn: 'S-B22a' },
      // S-G7. `seo#sites` — the site list and the per-site uptime monitor.
      // Flipped from disabled by the session that built the screen, which is the
      // one edit this file's own docstring says a module's session has to make.
      { label: 'Sites', to: '/seo/sites', arrivesIn: 'S-B30' },
      { label: 'Keywords', to: '/seo/keywords', arrivesIn: 'S-B24' },
      { label: 'Technical audit', to: '/seo/audit', arrivesIn: 'S-B23' },
      { label: 'Content', to: '/seo/content', arrivesIn: 'S-B25' },
      { label: 'Approvals', to: '/seo/approvals', arrivesIn: 'S-B27' },
      // S-B29. `seo#triggers` — when AugRank runs, and the unattended conditions
      // that may start a pass. Below Approvals rather than beside Settings,
      // because it is the SEO module's own automation rather than an
      // organisation-wide preference: an agency sets it per site.
      { label: 'Run triggers', to: '/seo/triggers', arrivesIn: 'S-B29' },
      // S-B32c. The viewer over S-B29's persisted trail. Below Run triggers
      // because the two are read in that order: a tenant sets when AugRank runs,
      // and then reads what it concluded when it did.
      { label: 'Reasoning trail', to: '/seo/trail', arrivesIn: 'S-B32c' },
      { label: 'Settings', to: '/settings/organisation', arrivesIn: 'S-B8' },
    ],
  },
]

/**
 * Screens inside a module that the sidebar deliberately does not list.
 *
 * S-G5, and two entries. `/seo/publish-targets` is `seo.html#targets` —
 * a real screen over five live endpoints — and S-D3 approved eight SEO sections,
 * none of which is it. Adding a ninth would be inventing information
 * architecture, which is the call `panels.ts` made when it folded the webhook
 * endpoints into Notifications rather than adding a settings panel nobody drew.
 *
 * But *unlisted* is not *nameless*: without this the breadcrumb fell back to
 * **Overview** on a screen that is not the overview, which is the shell telling
 * a small lie about where you are. So the navigation model keeps the label and
 * `sectionForPath` reads both tables — the sidebar renders `MODULES` only, and
 * still shows exactly the eight sections that were approved.
 */
export const SUBSCREENS: readonly NavSection[] = [
  { label: 'Where we publish', to: '/seo/publish-targets', arrivesIn: 'S-G5' },
  // S-B20. `outreach.html#compose`. The same call for the same reason: S-D3
  // approved seven Outreach sections and *What we say* is not one of them. It is
  // reached from the Leads header and from a lead's own score panel, which is
  // where a tenant is when they start wondering what the message says.
  //
  // **A lead's own page gets no entry here**, deliberately. `/outreach/leads/:id`
  // already matches the sidebar's *Leads* by prefix, so the breadcrumb is right
  // without one — and a second entry at the same `to` would make `sectionForPath`
  // pick between two equal-length matches by whichever `filter` happened to see
  // first, which is a breadcrumb decided by array order.
  { label: 'What we say', to: '/outreach/compose', arrivesIn: 'S-B20' },
  // S-G9. `outreach.html#post`. The same call for the same reason, on the other
  // module: S-D3 approved seven Outreach sections and *Publish to LinkedIn* is
  // not one of them. It is reached from the LinkedIn card in Settings →
  // Connections, which is where a tenant goes when they are thinking about
  // their LinkedIn account rather than about their pipeline.
  { label: 'Publish to LinkedIn', to: '/outreach/post', arrivesIn: 'S-G9' },
]

/** Where the shell remembers which module you were last in. */
export const MODULE_STORAGE_KEY = 'augrank.module'

export function moduleById(id: ModuleId): ModuleDefinition {
  const found = MODULES.find((module) => module.id === id)
  // Total by construction — `ModuleId` is the union of the ids above — but a
  // non-null assertion here would be the one place a typo in that union became
  // a blank sidebar instead of an error.
  if (!found) throw new Error(`No module definition for "${id}"`)
  return found
}

/**
 * Which module a path belongs to.
 *
 * `/settings` is shared: it is one organisation's settings whichever module you
 * reached it from, so it does not decide the module and the caller's remembered
 * choice stands. Returning null rather than defaulting is what lets the shell
 * keep the switcher where the user left it while they are in settings.
 */
export function moduleForPath(pathname: string): ModuleId | null {
  if (pathname === '/outreach' || pathname.startsWith('/outreach/')) return 'outreach'
  if (pathname === '/seo' || pathname.startsWith('/seo/')) return 'seo'
  return null
}

/**
 * The section a path is inside, if the navigation model names one.
 *
 * Added in S-B19, because the Inbox is the **first** section route in the
 * product and the shell's breadcrumb had been hardcoding *Overview* for
 * everything outside `/settings` — correct while every module had one screen,
 * and a lie the moment a second one landed. Derived from the same table the
 * sidebar renders, so a section that gets a route gets a breadcrumb with no
 * second edit.
 *
 * The **longest** match wins: `/outreach` is a prefix of `/outreach/inbox`, and
 * matching the shorter one first would put every future section under Overview
 * again. `null` for a path the model does not name — `/billing/*` is inside the
 * shell and is not a module section, and the caller decides what to say about
 * that rather than this file guessing.
 */
export function sectionForPath(pathname: string): NavSection | null {
  const matches = [...MODULES.flatMap((module) => module.sections), ...SUBSCREENS].filter(
    (section) =>
      section.to !== undefined &&
      (pathname === section.to || pathname.startsWith(`${section.to}/`)),
  )
  return matches.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0] ?? null
}

/**
 * The remembered module, falling back to SEO.
 *
 * SEO rather than Outreach because it is the module the product leads with and
 * the one a trial starts in (REQUIREMENTS §6). Any stored value that is not a
 * module id is ignored — `localStorage` is user-writable, and a typo there
 * should not be able to produce a shell with no navigation.
 */
export function rememberedModule(storage: Pick<Storage, 'getItem'>): ModuleId {
  const stored = storage.getItem(MODULE_STORAGE_KEY)
  return stored === 'outreach' || stored === 'seo' ? stored : 'seo'
}
